import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, Activity, GitBranch, DollarSign, Bell, History,
  BarChart3, Zap, Play, Pause, RotateCcw, Download
} from 'lucide-react';

const PAPER_SAFE_MODE = true;
const INITIAL_CAPITAL = 1000000; // ₹10 Lakh

/* ========================= L8 CAPITAL GOVERNANCE ========================= */
const CAPITAL_GOVERNANCE_DEFAULT = {
  mode: 'PAPER', // RESEARCH | PAPER | LIVE
  riskPerTrade: 0.005,        // 0.5%
  dailyLossLimit: 0.02,       // 2%
  maxPortfolioHeat: 0.25,
  capitalBuckets: {
    CORE: 0.6,
    TACTICAL: 0.3,
    EXPLORE: 0.1
  },
  killSwitch: {
    state: 'NORMAL', // NORMAL | CAUTION | HALT | LOCKDOWN
    reason: null
  }
};

const BASE_RISK = {
  AGGRESSIVE: 0.01,
  CAUTIOUS: 0.007,
  PROTECTIVE: 0.005,
  DEFENSIVE: 0.004,
  'RISK-OFF': 0
};

const generateDecisionId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2)}`;


/* ========================= CONSTANTS ========================= */
const ARCH_COLORS = {
  slate: "bg-slate-900/70 border border-slate-500",
  blue: "bg-blue-900/70 border border-blue-500",
  cyan: "bg-cyan-900/70 border border-cyan-500",
  green: "bg-green-900/70 border border-green-500",
  purple: "bg-purple-900/70 border border-purple-500",
  pink: "bg-pink-900/70 border border-pink-500",
  orange: "bg-orange-900/70 border border-orange-500"
};

const FSM_COLORS = {
  TREND: "text-green-400",
  ACCUMULATION: "text-cyan-400",
  EXHAUSTION: "text-orange-400",
  DISTRIBUTION: "text-red-400",
  FLAT: "text-slate-400"
};

const FONT_MODES = {
  ui: { base: "font-sans tracking-normal", heading: "font-sans font-bold", numeric: "tabular-nums" },
  paper: { base: "font-serif tracking-tight", heading: "font-serif font-semibold", numeric: "tabular-nums" }
};

const FONT = PAPER_SAFE_MODE ? FONT_MODES.paper : FONT_MODES.ui;

const POSITION_POLICY = {
  AGGRESSIVE: 0.25, CAUTIOUS: 0.15, PROTECTIVE: 0.08, DEFENSIVE: 0.05, 'RISK-OFF': 0
};

const MAX_EXPOSURE = {
  AGGRESSIVE: 0.7, CAUTIOUS: 0.5, PROTECTIVE: 0.3, DEFENSIVE: 0.25, 'RISK-OFF': 0
};

/* ========================= COMPONENTS ========================= */
const StatusIndicator = ({ status }) => {
  const color = status === 'STREAMING' ? 'bg-green-400' : 'bg-slate-400';
  return <span className={`inline-block w-2 h-2 rounded-full ${color} animate-pulse`} />;
};

const AlertBadge = ({ count }) => (
  count > 0 ? (
    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
      {count}
    </span>
  ) : null
);

/* ========================= MAIN COMPONENT ========================= */
const AlphaGenCQF = () => {
  const [stocks] = useState({
    RELIANCE: { name: 'Reliance Industries', basePrice: 1350, sector: 'Energy' },
    ADANIPORTS: { name: 'Adani Ports', basePrice: 1350, sector: 'Infrastructure' },
    LT: { name: 'L&T', basePrice: 3700, sector: 'Engineering' },
    ICICIBANK: { name: 'ICICI Bank', basePrice: 1250, sector: 'Banking' },
    WIPRO: { name: 'Wipro', basePrice: 250, sector: 'IT' }
  });

  // Core State
  const [liveData, setLiveData] = useState({
    RELIANCE: { price: 1390, change: 0, volume: 1200000, high: 1390, low: 1390, open: 1390 },
    ADANIPORTS: { price: 1360, change: 0, volume: 850000, high: 1360, low: 1360, open: 1360 },
    LT: { price: 3740, change: 0, volume: 620000, high: 3740, low: 3740, open: 3740 },
    ICICIBANK: { price: 1350, change: 0, volume: 1800000, high: 1350, low: 1350, open: 1350 },
    WIPRO: { price: 240, change: 0, volume: 2100000, high: 240, low: 240, open: 240 }
  });

  const [processedData, setProcessedData] = useState({
    totalVolume: 0, avgChange: 0, bullishCount: 0, bearishCount: 0
  });

  const [traitsSchema, setTraitsSchema] = useState({
    momentum: { score: 0 }, volatility: { score: 0 }, liquidity: { score: 0 }, strength: { score: 0 }
  });

  const [marketState, setMarketState] = useState({});

  const [adaptiveSchema, setAdaptiveSchema] = useState({
    posture: 'DEFENSIVE', confidence: 0, positionSize: 0, portfolioHeat: 0,
    dominantRegime: 'FLAT', thresholds: { stopLoss: -2, targetProfit: 2, volumeMin: 500000 }
  });

  const [decisionEngine, setDecisionEngine] = useState({
    recommendations: [], marketSentiment: 'NEUTRAL'
  });

  // NEW: Paper Trading State
  const [paperTrading, setPaperTrading] = useState({
    enabled: true,
    cash: INITIAL_CAPITAL,
    positions: {},
    orders: [],
    pnl: 0,
    totalValue: INITIAL_CAPITAL,
    roi: 0
  });

  /* ========================= L8 STATE ========================= */
const [capitalGovernance, setCapitalGovernance] = useState(
  CAPITAL_GOVERNANCE_DEFAULT
);

const [decisionLedger, setDecisionLedger] = useState([]);


  // NEW: Backtesting State
  const [backtest, setBacktest] = useState({
    running: false,
    results: null,
    sharpeRatio: 0,
    maxDrawdown: 0,
    winRate: 0,
    totalTrades: 0,
    history: []
  });

  // NEW: Alert System
  const [alerts, setAlerts] = useState([]);
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  // NEW: Performance Metrics
  const [performance, setPerformance] = useState({
    dailyReturns: [],
    cumulativeReturn: 0,
    volatility: 0,
    sharpe: 0,
    maxDD: 0
  });

  // NEW: Historical Price Data (for backtesting)
  const [historicalData, setHistoricalData] = useState({});

  /* ========================= TRAITS COMPUTATION ========================= */
  useEffect(() => {
    setTraitsSchema({
      momentum: { score: Math.min(10, Math.max(0, processedData.avgChange * 5)) },
      volatility: { score: Math.min(10, processedData.bearishCount * 2) },
      liquidity: { score: Math.min(10, processedData.totalVolume / 5_000_000) },
      strength: { score: Math.min(10, (processedData.bullishCount - processedData.bearishCount + 5) * 1.2) }
    });
  }, [processedData]);

  /* ========================= FSM EMISSION ========================= */
  useEffect(() => {
    const next = {};
    Object.keys(stocks).forEach(symbol => {
      const { momentum: m, volatility: v, liquidity: l, strength: s } = traitsSchema;
      let state = 'FLAT', conf = 0.5, reason = 'NEUTRAL';

      // Advanced FSM Logic
      if (m.score > 7 && s.score > 7 && v.score < 6) {
        state = 'TREND'; conf = 0.8; reason = 'MOMENTUM_STRENGTH_ALIGN';
      } else if (m.score > 5 && l.score > 6 && v.score < 5) {
        state = 'ACCUMULATION'; conf = 0.65; reason = 'SMART_MONEY_BUILDUP';
      } else if (v.score > 7) {
        state = 'EXHAUSTION'; conf = 0.7; reason = 'VOLATILITY_SPIKE';
      } else if (m.score < 3 && s.score < 4) {
        state = 'DISTRIBUTION'; conf = 0.75; reason = 'WEAKNESS_DETECTED';
      }

      const prev = marketState[symbol];
      next[symbol] = {
        state,
        confidence: conf,
        state_age: prev?.state === state ? prev.state_age + 1 : 1,
        reason_code: reason
      };
    });
    setMarketState(next);
  }, [traitsSchema, stocks, marketState]);

  /* ========================= ADAPTIVE POLICY ========================= */
  useEffect(() => {
    const entries = Object.values(marketState);
    if (!entries.length) return;

    const regimeCounts = {};
    entries.forEach(e => regimeCounts[e.state] = (regimeCounts[e.state] || 0) + 1);
    const dominantRegime = Object.entries(regimeCounts).sort((a, b) => b[1] - a[1])[0][0];

    const policy = {
      TREND: 'AGGRESSIVE', ACCUMULATION: 'CAUTIOUS', EXHAUSTION: 'PROTECTIVE',
      DISTRIBUTION: 'RISK-OFF', FLAT: 'DEFENSIVE'
    }[dominantRegime];

    const avgConf = entries.reduce((s, e) => s + e.confidence, 0) / entries.length;
    const base = POSITION_POLICY[policy];

    setAdaptiveSchema(prev => ({
      ...prev, posture: policy, dominantRegime, confidence: avgConf, positionSize: base * avgConf
    }));
  }, [marketState]);

  /* ========================= ALERT SYSTEM ========================= */
  const addAlert = (type, message, symbol = null) => {
    const newAlert = {
      id: Date.now(),
      type, // 'BUY', 'SELL', 'WARNING', 'INFO'
      message,
      symbol,
      timestamp: new Date(),
      read: false
    };
    setAlerts(prev => [newAlert, ...prev].slice(0, 50)); // Keep last 50
    setUnreadAlerts(prev => prev + 1);
  };

  /* ========================= PAPER TRADING FUNCTIONS ========================= */
  const executeTrade = (symbol, action, quantity, price) => {
    if (!paperTrading.enabled) return;

    const cost = quantity * price;
    const commission = cost * 0.001; // 0.1% commission
    
    const decisionId = generateDecisionId();

    setDecisionLedger(prev => [{
      decisionId,
      symbol,
      action,
      quantity,
      price,
      regime: marketState[symbol]?.state,
      confidence: marketState[symbol]?.confidence,
      posture: adaptiveSchema.posture,
      approvedBy: 'L8_CAPITAL_GOVERNANCE',
      timestamp: new Date().toISOString()
    }, ...prev]);

    setPaperTrading(prev => {
      const newState = { ...prev };

      if (action === 'BUY') {
        if (prev.cash < cost + commission) {
          addAlert('WARNING', `Insufficient funds to buy ${quantity} ${symbol}`, symbol);
          return prev;
        }
        newState.cash -= (cost + commission);
        newState.positions[symbol] = {
          quantity: (prev.positions[symbol]?.quantity || 0) + quantity,
          avgPrice: ((prev.positions[symbol]?.avgPrice || 0) * (prev.positions[symbol]?.quantity || 0) + cost) / 
                   ((prev.positions[symbol]?.quantity || 0) + quantity)
        };
        addAlert('BUY', `Bought ${quantity} ${symbol} @ ₹${price.toFixed(2)}`, symbol);
      } else if (action === 'SELL') {
        const held = prev.positions[symbol]?.quantity || 0;
        if (held < quantity) {
          addAlert('WARNING', `Insufficient ${symbol} to sell`, symbol);
          return prev;
        }
        newState.cash += (cost - commission);
        newState.positions[symbol].quantity -= quantity;
        if (newState.positions[symbol].quantity === 0) {
          delete newState.positions[symbol];
        }
        addAlert('SELL', `Sold ${quantity} ${symbol} @ ₹${price.toFixed(2)}`, symbol);
      }

      newState.orders.push({
        timestamp: new Date(),
        symbol, action, quantity, price, commission
      });

      return newState;
    });
  };
  
  /* ========================= L8 POSITION SIZING ========================= */
  const calculatePositionSize = ({
    equity,
    posture,
    regimeConfidence,
    modelAgreement,
    volatility,
    capitalGov
  }) => {
    if (capitalGov.killSwitch.state !== 'NORMAL') return 0;

  const baseRisk = BASE_RISK[posture] || 0;
  const volFactor = Math.max(0.3, 1 - volatility / 100);

  const rawSize =
    equity *
    baseRisk *
    regimeConfidence *
    modelAgreement *
    volFactor;

  const maxAllowed = equity * capitalGov.riskPerTrade;
  return Math.min(rawSize, maxAllowed);
};



  /* ========================= PORTFOLIO OPTIMIZATION ========================= */
  const optimizePortfolio = () => {
    const recommendations = {};
    const maxExposure = MAX_EXPOSURE[adaptiveSchema.posture];
    const availableCapital = paperTrading.totalValue * maxExposure;

    Object.keys(stocks).forEach(symbol => {
      const fsm = marketState[symbol];
      if (!fsm) return;

      const data = liveData[symbol];
      const shouldBuy = fsm.state === 'TREND' || fsm.state === 'ACCUMULATION';
      
      if (shouldBuy && fsm.confidence > 0.6) {
        const modelAgreement = fsm.state === 'TREND' ? 0.9 : 0.6;
        const volatility = performance.volatility || 20;

        const positionValue = calculatePositionSize({
          equity: paperTrading.totalValue,
          posture: adaptiveSchema.posture,
          regimeConfidence: fsm.confidence,
          modelAgreement,
          volatility,
          capitalGov: capitalGovernance
        });

        const targetValue = availableCapital * 0.2;
        const quantity = Math.floor(targetValue / data.price);

        if (quantity > 0) {
          recommendations[symbol] = {
            action: 'BUY',
            quantity,
            reason: `${fsm.state} regime with ${(fsm.confidence * 100).toFixed(0)}% confidence`,
            allocation: ((positionValue / paperTrading.totalValue) * 100).toFixed(2)

          };
        }
      }
    });

    return recommendations;
  };

  /* ========================= PERFORMANCE CALCULATION ========================= */
  useEffect(() => {
    // Calculate portfolio value
    let positionsValue = 0;
    Object.entries(paperTrading.positions).forEach(([symbol, pos]) => {
      positionsValue += pos.quantity * liveData[symbol].price;
    });

    const totalValue = paperTrading.cash + positionsValue;
    const pnl = totalValue - INITIAL_CAPITAL;
    const roi = (pnl / INITIAL_CAPITAL) * 100;

    setPaperTrading(prev => ({ ...prev, totalValue, pnl, roi }));

    // Calculate performance metrics
    const returns = [(totalValue - INITIAL_CAPITAL) / INITIAL_CAPITAL];
    const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length;
    const volatility = Math.sqrt(variance) * Math.sqrt(252); // Annualized
    const sharpe = avgReturn / (volatility || 1);

    setPerformance(prev => ({
      ...prev,
      dailyReturns: returns,
      cumulativeReturn: roi,
      volatility: volatility * 100,
      sharpe
    }));
  }, [liveData, paperTrading.cash, paperTrading.positions]);

  /* ========================= L8 KILL SWITCH ========================= */
  useEffect(() => {
    if (paperTrading.roi < -capitalGovernance.dailyLossLimit * 100) {
      setCapitalGovernance(prev => ({
        ...prev,
        killSwitch: {
          state: 'HALT',
          reason: 'DAILY_LOSS_LIMIT_BREACHED'
        }
      }));

      addAlert(
        'WARNING',
        'KILL SWITCH ACTIVATED: Daily loss limit breached'
      );
    }
  }, [paperTrading.roi]);
 

  /* ========================= BACKTESTING ENGINE ========================= */
  const runBacktest = () => {
    setBacktest(prev => ({ ...prev, running: true }));
    
    // Simulate 30 days of trading
    const results = {
      trades: [],
      equity: [INITIAL_CAPITAL]
    };

    let capital = INITIAL_CAPITAL;
    let positions = {};
    let wins = 0, losses = 0;

    for (let day = 0; day < 30; day++) {
      Object.keys(stocks).forEach(symbol => {
        const randomMove = (Math.random() - 0.5) * 0.05; // ±5%
        const price = stocks[symbol].basePrice * (1 + randomMove);
        
        if (randomMove > 0.02 && capital > price * 10) {
          // BUY
          const qty = 10;
          capital -= price * qty;
          positions[symbol] = { qty, price };
          results.trades.push({ day, symbol, action: 'BUY', price, qty });
        } else if (positions[symbol] && randomMove < -0.02) {
          // SELL
          const { qty, price: buyPrice } = positions[symbol];
          capital += price * qty;
          const profit = (price - buyPrice) * qty;
          profit > 0 ? wins++ : losses++;
          delete positions[symbol];
          results.trades.push({ day, symbol, action: 'SELL', price, qty, profit });
        }
      });
      results.equity.push(capital);
    }

    const finalValue = capital + Object.values(positions).reduce((sum, p) => 
      sum + p.qty * stocks[p.symbol]?.basePrice || 0, 0);
    
    const totalReturn = ((finalValue - INITIAL_CAPITAL) / INITIAL_CAPITAL) * 100;
    const maxDD = Math.max(...results.equity.map((v, i) => 
      (Math.max(...results.equity.slice(0, i + 1)) - v) / Math.max(...results.equity.slice(0, i + 1)) * 100
    ));

    setBacktest({
      running: false,
      results,
      sharpeRatio: totalReturn / (maxDD || 1),
      maxDrawdown: maxDD,
      winRate: (wins / (wins + losses)) * 100 || 0,
      totalTrades: wins + losses,
      history: results.equity
    });

    addAlert('INFO', `Backtest completed: ${totalReturn.toFixed(2)}% return`);
  };

  /* ========================= SIMULATION LOOP ========================= */
  useEffect(() => {
    const interval = setInterval(() => {
      let bullish = 0, bearish = 0, volume = 0, changeSum = 0;
      const updated = {}, recs = [];

      Object.keys(stocks).forEach(symbol => {
        const prev = liveData[symbol];
        const price = prev.price + (Math.random() - 0.5) * 2;
        const change = ((price - stocks[symbol].basePrice) / stocks[symbol].basePrice) * 100;

        updated[symbol] = {
          price, change,
          volume: prev.volume + Math.random() * 200000,
          high: Math.max(prev.high, price),
          low: Math.min(prev.low, price),
          open: prev.open
        };

        volume += updated[symbol].volume;
        changeSum += change;
        change > 0 ? bullish++ : bearish++;

        const fsm = marketState[symbol];
        if (change > 0.5 && fsm) {
          const divergent = fsm.state !== adaptiveSchema.dominantRegime;
          recs.push({
            symbol,
            action: divergent ? 'WATCH' : 'STRONG BUY',
            fsm_state: fsm.state,
            fsm_conf: fsm.confidence,
            fsm_reason: fsm.reason_code,
            divergent,
            price: price.toFixed(2)
          });
        }

        // Auto-trade in paper mode
        if (paperTrading.enabled && fsm?.state === 'TREND' && fsm.confidence > 0.7) {
          const currentPos = paperTrading.positions[symbol]?.quantity || 0;
          if (currentPos === 0 && change > 1) {
            const qty = Math.floor((paperTrading.cash * 0.1) / price);
            if (qty > 0) executeTrade(symbol, 'BUY', qty, price);
          }
        }
      });

      setLiveData(updated);
      setProcessedData({ totalVolume: volume, avgChange: changeSum / 5, bullishCount: bullish, bearishCount: bearish });
      setDecisionEngine({ recommendations: recs, marketSentiment: changeSum > 0 ? 'BULLISH' : 'BEARISH' });
    }, 3000);

    return () => clearInterval(interval);
  }, [liveData, marketState, adaptiveSchema, paperTrading, stocks]);

  /* ========================= UI RENDER ========================= */
  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white p-6 ${FONT.base}`}>
      
      {/* HEADER */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className={`text-4xl ${FONT.heading} bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent`}>
            AlphaGenCQF Production v2.0
          </h1>
          <div className="text-sm text-cyan-300">Research Cockpit · Regime-Adaptive · Market-Cognition</div>
          <div className="text-xs text-slate-400 italic">Production-Ready · Full Feature Stack</div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${
            decisionEngine.marketSentiment === 'BULLISH' ? 'text-green-400' : 'text-red-400'
          }`}>
            {decisionEngine.marketSentiment}
          </div>
          <div className="text-xs text-slate-400">Market Sentiment</div>
        </div>
      </div>

      {/* ARCHITECTURE */}
      <div className="mb-6 rounded-xl border border-cyan-500/40 p-5 bg-slate-900/60">
        <div className="flex gap-4 overflow-x-auto">
          {[
            ["L1", "Historical", "slate"],
            ["L2", "Live NSE", "blue"],
            ["L3", "Processed", "cyan"],
            ["L4", "Traits", "green"],
            ["L5", "Adaptive", "purple"],
            ["L6", "Decision", "pink"],
            ["L7", "Cockpit", "orange"]
          ].map(([l, t, c]) => (
            <div key={l} className={`w-32 h-32 rounded-xl p-3 ${ARCH_COLORS[c]}`}>
              <div className="text-xs opacity-70">{l}</div>
              <div className="font-bold mt-2 text-sm">{t}</div>
            </div>
          ))}
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-12 gap-4 mb-6">
        
        {/* PAPER TRADING */}
        <div className="col-span-4 bg-gradient-to-br from-emerald-950/80 to-green-950/80 border-2 border-green-500 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-lg">📊 PAPER TRADING</div>
            <StatusIndicator status="STREAMING" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-300">Portfolio Value:</span>
              <span className="font-bold text-xl">₹{(paperTrading.totalValue / 100000).toFixed(2)}L</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">Cash:</span>
              <span className="font-bold">₹{(paperTrading.cash / 100000).toFixed(2)}L</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">P&L:</span>
              <span className={`font-bold ${paperTrading.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {paperTrading.pnl >= 0 ? '+' : ''}₹{(paperTrading.pnl / 1000).toFixed(2)}K
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">ROI:</span>
              <span className={`font-bold ${paperTrading.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {paperTrading.roi >= 0 ? '+' : ''}{paperTrading.roi.toFixed(2)}%
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-green-700">
              <div className="text-xs text-slate-400 mb-2">Positions:</div>
              {Object.entries(paperTrading.positions).map(([sym, pos]) => (
                <div key={sym} className="text-xs flex justify-between mb-1">
                  <span>{sym}:</span>
                  <span>{pos.quantity} @ ₹{pos.avgPrice.toFixed(2)}</span>
                </div>
              ))}
              {Object.keys(paperTrading.positions).length === 0 && (
                <div className="text-xs text-slate-500 italic">No open positions</div>
              )}
            </div>
          </div>
        </div>

        {/* PERFORMANCE METRICS */}
        <div className="col-span-4 bg-gradient-to-br from-blue-950/80 to-indigo-950/80 border-2 border-blue-500 rounded-xl p-4">
          <div className="font-bold text-lg mb-3">📈 PERFORMANCE</div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-300">Sharpe Ratio:</span>
              <span className="font-bold">{performance.sharpe.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">Volatility:</span>
              <span className="font-bold">{performance.volatility.toFixed(2)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-300">Cumulative Return:</span>
              <span className={`font-bold ${performance.cumulativeReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {performance.cumulativeReturn >= 0 ? '+' : ''}{performance.cumulativeReturn.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>

        {/* ALERTS */}
        <div className="col-span-4 bg-gradient-to-br from-orange-950/80 to-red-950/80 border-2 border-orange-500 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-lg flex items-center gap-2 relative">
              <Bell size={20} />
              ALERTS
              <AlertBadge count={unreadAlerts} />
            </div>
            <button
              onClick={() => setUnreadAlerts(0)}
              className="text-xs bg-orange-700 px-2 py-1 rounded hover:bg-orange-600"
            >
              Mark Read
            </button>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {alerts.slice(0, 5).map(alert => (
              <div key={alert.id} className={`text-xs p-2 rounded border ${
                alert.type === 'BUY' ? 'border-green-600 bg-green-950/50' :
                alert.type === 'SELL' ? 'border-red-600 bg-red-950/50' :
                alert.type === 'WARNING' ? 'border-yellow-600 bg-yellow-950/50' :
                'border-blue-600 bg-blue-950/50'
              }`}>
                <div className="font-bold">{alert.symbol || 'SYSTEM'}</div>
                <div>{alert.message}</div>
                <div className="text-xs text-slate-400 mt-1">
                  {alert.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}
            {alerts.length === 0 && <div className="text-xs text-slate-500 italic">No alerts</div>}
          </div>
        </div>

        {/* MARKET */}
        <div className="col-span-3 bg-cyan-950/70 border border-cyan-500 rounded-xl p-4">
          <div className="font-bold mb-2">L3: MARKET</div>
          <div className={`text-2xl font-bold ${processedData.avgChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {processedData.avgChange >= 0 ? '+' : ''}{processedData.avgChange.toFixed(2)}%
          </div>
          <div className="mt-2 text-sm">Volume: {(processedData.totalVolume / 1e6).toFixed(2)}M</div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div className="text-green-400">Bullish: {processedData.bullishCount}</div>
            <div className="text-red-400">Bearish: {processedData.bearishCount}</div>
          </div>
        </div>

        {/* TRAITS */}
        <div className="col-span-3 bg-green-950/70 border border-green-500 rounded-xl p-4">
          <div className="font-bold mb-2">L4: TRAITS</div>
          <div className="space-y-1">
            {Object.entries(traitsSchema).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="text-slate-300">{k}:</span>
                <span className="font-bold">{v.score.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* FSM */}
        <div className="col-span-3 bg-yellow-950/70 border border-yellow-500 rounded-xl p-4">
          <div className="font-bold mb-2">L4.5: FSM</div>
          <div className="text-yellow-300 mb-2 text-sm">
            Regime: <b>{adaptiveSchema.dominantRegime}</b>
          </div>
          <div className="space-y-1">
            {Object.entries(marketState).map(([s, v]) => (
              <div key={s} className="text-xs flex justify-between">
                <span>{s}:</span>
                <span className={FSM_COLORS[v.state]}>{v.state}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ADAPTIVE */}
        <div className="col-span-3 bg-purple-950/70 border border-purple-500 rounded-xl p-4">
          <div className="font-bold mb-2">L5: ADAPTIVE</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Posture:</span>
              <span className="font-bold">{adaptiveSchema.posture}</span>
            </div>
            <div className="flex justify-between">
              <span>Confidence:</span>
              <span className="font-bold">{(adaptiveSchema.confidence * 100).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span>Position Size:</span>
              <span className="font-bold">{(adaptiveSchema.positionSize * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* DECISION */}
        <div className="col-span-6 bg-pink-950/70 border border-pink-500 rounded-xl p-4">
          <div className="font-bold mb-2">L6: DECISION ENGINE</div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {decisionEngine.recommendations.map((r, i) => (
              <div key={i} className={`p-2 rounded text-sm border ${
                r.divergent ? 'border-orange-500 bg-orange-950/30' : 'border-green-500 bg-green-950/30'
              }`}>
                <div className="flex justify-between items-center">
                  <span className="font-bold">{r.symbol}</span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    r.action === 'STRONG BUY' ? 'bg-green-600' : 'bg-yellow-600'
                  }`}>
                    {r.action}
                  </span>
                </div>
                <div className="text-xs text-slate-300 mt-1">
                  {r.fsm_state} · {(r.fsm_conf * 100).toFixed(0)}% · ₹{r.price}
                </div>
              </div>
            ))}
            {decisionEngine.recommendations.length === 0 && (
              <div className="text-xs text-slate-500 italic">No active recommendations</div>
            )}
          </div>
        </div>

        {/* BACKTEST */}
        <div className="col-span-6 bg-indigo-950/70 border border-indigo-500 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold">🔬 BACKTEST ENGINE</div>
            <button
              onClick={runBacktest}
              disabled={backtest.running}
              className="bg-indigo-700 hover:bg-indigo-600 px-3 py-1 rounded text-sm flex items-center gap-2 disabled:opacity-50"
            >
              <Play size={14} />
              {backtest.running ? 'Running...' : 'Run Backtest'}
            </button>
          </div>
          {backtest.results ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Sharpe Ratio:</span>
                <span className="font-bold">{backtest.sharpeRatio.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Max Drawdown:</span>
                <span className="font-bold text-red-400">{backtest.maxDrawdown.toFixed(2)}%</span>
              </div>
              <div className="flex justify-between">
                <span>Win Rate:</span>
                <span className="font-bold text-green-400">{backtest.winRate.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span>Total Trades:</span>
                <span className="font-bold">{backtest.totalTrades}</span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic">No backtest results yet</div>
          )}
        </div>
      </div>

      <div className="min-h-screen bg-slate-950 text-white p-6">
        <h1 className="text-3xl font-bold text-cyan-400">AlphaGen CQF v3 — Fund-Grade Alpha Engine</h1>
        <p className="text-sm text-slate-400 mb-4">Capital-Governed · Audit-Ready · Risk-Safe</p>


      {/* CONTROLS */}
      <div className="bg-slate-900/80 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-400">
            <StatusIndicator status="STREAMING" /> System Operational · Last Update: {new Date().toLocaleTimeString()}
          </div>
          <div className="flex gap-3">
            <button className="bg-green-700 hover:bg-green-600 px-4 py-2 rounded font-bold text-sm">
              AUTO-TRADE: {paperTrading.enabled ? 'ON' : 'OFF'}
            </button>
            <button className="bg-blue-700 hover:bg-blue-600 px-4 py-2 rounded font-bold text-sm flex items-center gap-2">
              <Download size={16} />
              Export Data
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AlphaGenCQF;